function getCartDrawerExperimentMode() {
  var root = document.documentElement;
  if (!root) return null;

  if (root.dataset) {
    if (root.dataset.cartExp === 'v1') return 'v1';
    if (root.dataset.cartExp === 'control') return 'control';
  }
  if (root.classList) {
    if (root.classList.contains('cart-exp-control')) return 'control';
    if (root.classList.contains('cart-exp-v1') || root.classList.contains('cro-v1')) return 'v1';
  }

  return null;
}

function isCartDrawerExperimentActive() {
  return getCartDrawerExperimentMode() !== null;
}

function getCartDrawerTemplateId(mode) {
  if (mode === 'v1') return 'cart-drawer-template-v1';
  if (mode === 'control') return 'cart-drawer-template-control';
  return null;
}

function getCartDrawerParsedHTML(html) {
  if (!html) return null;
  if (typeof html === 'string') return new DOMParser().parseFromString(html, 'text/html');
  return html;
}

function getExperimentCartSectionSource(html, selector) {
  var mode = getCartDrawerExperimentMode();
  var templateId = getCartDrawerTemplateId(mode);
  var parsed = getCartDrawerParsedHTML(html);
  if (!templateId || !parsed || !selector) return null;

  var template = parsed.getElementById(templateId);
  if (!template || !template.content) return null;

  return template.content.querySelector(selector);
}

function getCartSectionInnerHTML(html, selector) {
  var parsed = getCartDrawerParsedHTML(html);
  if (!parsed || !selector) return '';

  var experimentSource = getExperimentCartSectionSource(parsed, selector);
  if (experimentSource) return experimentSource.innerHTML;

  var source = parsed.querySelector(selector);
  return source ? source.innerHTML : '';
}

function dispatchCartModeSignal() {
  var event;
  if (typeof CustomEvent === 'function') {
    event = new CustomEvent('catchmaster:cart-mode-signal');
  } else {
    event = document.createEvent('CustomEvent');
    event.initCustomEvent('catchmaster:cart-mode-signal', true, true);
  }
  document.dispatchEvent(event);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    if (isCartDrawerExperimentActive() || cartLink.dataset.cartDrawerBound === 'true') {
      return;
    }

    cartLink.dataset.cartDrawerBound = 'true';
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    if (isCartDrawerExperimentActive()) {
      this.style.setProperty('display', 'flex', 'important');
      this.style.setProperty('visibility', 'visible', 'important');
      this.style.setProperty('pointer-events', 'auto', 'important');

      const cartDrawerPanel = this.querySelector('#CartDrawer');
      if (cartDrawerPanel) {
        cartDrawerPanel.style.setProperty('display', 'block', 'important');
      }

    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (isCartDrawerExperimentActive()) {
      this.style.removeProperty('display');
      this.style.setProperty('visibility', 'hidden', 'important');
      this.style.setProperty('pointer-events', 'none', 'important');
    }
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (parsedState.item_count !== 0) {
      this.classList.remove('is-empty');
      const inner = this.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;

    const sectionCache = new Map();

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const sectionHTML = parsedState.sections && parsedState.sections[section.id];
      if (!sectionElement || !sectionHTML) return;

      let parsedSection = sectionCache.get(section.id);
      if (!parsedSection) {
        parsedSection = getCartDrawerParsedHTML(sectionHTML);
        sectionCache.set(section.id, parsedSection);
      }

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedSection, section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
      if (isCartDrawerExperimentActive()) dispatchCartModeSignal();
    }, 0);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return getCartSectionInnerHTML(html, selector);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    const parsed = getCartDrawerParsedHTML(html);
    return parsed ? parsed.querySelector(selector) : null;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    if (!isCartDrawerExperimentActive()) {
      super.onCartUpdate();
      return;
    }

    const refreshRequestId = (this._drawerRefreshRequestId || 0) + 1;
    this._drawerRefreshRequestId = refreshRequestId;

    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        if (refreshRequestId !== this._drawerRefreshRequestId) return;

        const html = getCartDrawerParsedHTML(responseText);
        const template = html.getElementById(getCartDrawerTemplateId(getCartDrawerExperimentMode()));
        const sourceRoot = template && template.content ? template.content : html;
        const sourceDrawer = sourceRoot.querySelector('cart-drawer');
        const targetDrawer = document.querySelector('cart-drawer');
        const selectors = ['cart-drawer-items', '.cart-drawer__footer'];

        if (targetDrawer && sourceDrawer) {
          targetDrawer.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
        }

        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = sourceRoot.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }

        dispatchCartModeSignal();
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionInnerHTML(html, selector, sectionCache, sectionName) {
    const cacheKey = sectionName || selector || 'cart-drawer-section';
    let parsedSection = sectionCache && sectionCache.get(cacheKey);

    if (!parsedSection) {
      parsedSection = getCartDrawerParsedHTML(html);
      if (sectionCache) sectionCache.set(cacheKey, parsedSection);
    }

    return getCartSectionInnerHTML(parsedSection, selector);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
